<template>
  <section class="lesson-kit">
    <!-- 선택한 부서/학기/과 요약 (style.css 의 .mall_main header 그리드를 피하려고 div 사용) -->
    <div class="lesson-kit__head">
      <!-- 콘텐츠 종류(division) 필터 -->
      <!-- 모바일은 두 줄로 나눠 함께 가로 스크롤, 그 이상은 줄 구분 없이 자연스럽게 줄바꿈 -->
      <div class="lesson-kit__chips" role="group" aria-label="콘텐츠 종류">
        <div
          v-for="(row, rowIdx) in chipRows"
          :key="rowIdx"
          class="lesson-kit__chip-row"
        >
          <button
            v-for="chip in row"
            :key="chip.division"
            type="button"
            class="lesson-kit__chip"
            :class="{ 'is-active': activeDivision === chip.division }"
            :aria-pressed="activeDivision === chip.division"
            @click="$emit('update:activeDivision', chip.division)"
          >
            {{ chip.label }}
            <span class="lesson-kit__chip-count">
              {{ chip.count.toLocaleString() }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 목록: division 구분 없이 한 그리드에, 종류는 썸네일 배지로 표시 -->
    <ul class="lesson-kit__grid" :class="{ 'is-loading': loading }">
      <li
        v-for="item in items"
        :key="`${item.division}-${item.content.id}`"
        class="lesson-kit__item"
      >
        <button
          type="button"
          class="lesson-kit__card"
          @click="$emit('open', item.content)"
        >
          <div class="lesson-kit__thumb">
            <img
              v-if="thumbnailOf(item.content)"
              :src="thumbnailOf(item.content)"
              :alt="item.content.title"
              loading="lazy"
            />
            <img
              v-else
              src="@/assets/img/null_img.jpg"
              :alt="item.content.title"
            />
            <!-- 특정 종류를 고른 경우엔 배지가 중복이므로 '전체'에서만 표시 -->
            <span
              v-if="!activeDivision && item.division"
              class="lesson-kit__badge"
            >
              {{ item.division }}
            </span>
          </div>

          <div class="lesson-kit__body">
            <span v-if="groupNamesOf(item.content)" class="lesson-kit__group">
              {{ groupNamesOf(item.content) }}
            </span>
            <h3 class="product_tt">{{ item.content.title }}</h3>
          </div>
        </button>

        <!-- 마우스오버 시 상세 정보 (키보드는 위 카드 버튼으로 접근하므로 aria-hidden) -->
        <div
          class="lesson-kit__overlay"
          aria-hidden="true"
          @click="$emit('open', item.content)"
        >
          <p class="lesson-kit__overlay-title">{{ item.content.title }}</p>

          <!-- 타겟그룹 -->
          <div
            v-if="item.content.targetGroupList?.length"
            class="lesson-kit__info"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="lesson-kit__info-icon"
            >
              <path
                d="M4.66663 5.33366H4.67329M8.66663 5.33366H8.67329M4.99996 8.66699C5.21721 8.88872 5.47653 9.06488 5.76271 9.18513C6.0489 9.30539 6.3562 9.36733 6.66663 9.36733C6.97705 9.36733 7.28436 9.30539 7.57054 9.18513C7.85673 9.06488 8.11604 8.88872 8.33329 8.66699M0.666626 6.66699C0.666626 7.45492 0.82182 8.23514 1.12335 8.96309C1.42488 9.69105 1.86683 10.3525 2.42399 10.9096C2.98114 11.4668 3.64257 11.9087 4.37053 12.2103C5.09848 12.5118 5.87869 12.667 6.66663 12.667C7.45456 12.667 8.23477 12.5118 8.96273 12.2103C9.69068 11.9087 10.3521 11.4668 10.9093 10.9096C11.4664 10.3525 11.9084 9.69105 12.2099 8.96309C12.5114 8.23514 12.6666 7.45492 12.6666 6.66699C12.6666 5.87906 12.5114 5.09885 12.2099 4.37089C11.9084 3.64294 11.4664 2.9815 10.9093 2.42435C10.3521 1.8672 9.69068 1.42524 8.96273 1.12372C8.23477 0.822187 7.45456 0.666992 6.66663 0.666992C5.87869 0.666992 5.09848 0.822187 4.37053 1.12372C3.64257 1.42524 2.98114 1.8672 2.42399 2.42435C1.86683 2.9815 1.42488 3.64294 1.12335 4.37089C0.82182 5.09885 0.666626 5.87906 0.666626 6.66699Z"
                stroke="currentColor"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="lesson-kit__info-list">
              <p
                v-for="(batch, index) in groupedTargetsOf(
                  item.content.targetGroupList,
                )"
                :key="index"
              >
                <b>{{ batch.names }}</b>{{ batch.suffix }}
              </p>
            </div>
          </div>

          <!-- 사용 장소 -->
          <div
            v-if="item.content.itemUsageContextList?.length"
            class="lesson-kit__info"
          >
            <svg
              width="10"
              height="14"
              viewBox="0 0 10 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="lesson-kit__info-icon"
            >
              <path
                d="M4.85714 0C7.53971 0 9.71429 2 9.71429 5.42857C9.71429 6.78971 8.94886 8.46571 8.02571 9.99257L7.87057 10.2454C7.81819 10.3292 7.76571 10.4125 7.71314 10.4951L7.55371 10.7411L7.47371 10.8626L7.31343 11.1023L7.15257 11.3366L6.99257 11.566L6.83371 11.7894L6.67657 12.0066L6.52171 12.2166L6.37 12.4194L6.22171 12.6143L6.00771 12.8914L5.80543 13.148L5.61686 13.3829L5.55771 13.456C5.17086 13.9143 4.52143 13.896 4.15686 13.456L3.97343 13.2291L3.77571 12.9794L3.63657 12.8014L3.49286 12.6146L3.26914 12.3191L3.11571 12.1126L2.95971 11.8991L2.80171 11.6789L2.642 11.4526L2.48143 11.2206L2.32086 10.9834C2.26752 10.9034 2.21419 10.8228 2.16086 10.7414L2.00143 10.4957L1.844 10.2457L1.68886 9.99286C0.765428 8.46571 0 6.78971 0 5.42857C0 2 2.17457 0 4.85714 0ZM4.85714 1.14286C2.67943 1.14286 1.14286 2.76629 1.14286 5.42857C1.14286 6.38429 1.64171 7.66057 2.46 9.07057L2.60371 9.31343L2.75314 9.55886L2.90857 9.80657L2.98829 9.93114L3.152 10.1814L3.32114 10.4337L3.49543 10.6874L3.67457 10.9423L3.85857 11.1983L3.95229 11.3269L4.14314 11.584L4.316 11.8123L4.40514 11.928C4.45855 11.997 4.52703 12.0528 4.60536 12.0912C4.68368 12.1297 4.76976 12.1496 4.857 12.1496C4.94424 12.1496 5.03032 12.1297 5.10864 12.0912C5.18697 12.0528 5.25546 11.997 5.30886 11.928L5.47114 11.7166L5.66429 11.4583L5.85314 11.2011L6.03743 10.9449L6.21714 10.6894L6.39143 10.4354C6.44895 10.351 6.50543 10.2669 6.56086 10.1829L6.72486 9.93229C6.85933 9.72391 6.98771 9.51781 7.11 9.314L7.25343 9.07114C8.07286 7.66 8.57143 6.38371 8.57143 5.42857C8.57143 2.76629 7.03486 1.14286 4.85714 1.14286ZM4.85714 2.57143C5.46335 2.57143 6.04473 2.81224 6.47339 3.2409C6.90204 3.66955 7.14286 4.25093 7.14286 4.85714C7.14286 5.46335 6.90204 6.04473 6.47339 6.47339C6.04473 6.90204 5.46335 7.14286 4.85714 7.14286C4.25093 7.14286 3.66955 6.90204 3.2409 6.47339C2.81224 6.04473 2.57143 5.46335 2.57143 4.85714C2.57143 4.25093 2.81224 3.66955 3.2409 3.2409C3.66955 2.81224 4.25093 2.57143 4.85714 2.57143ZM4.85714 3.71429C4.55404 3.71429 4.26335 3.83469 4.04902 4.04902C3.83469 4.26335 3.71429 4.55404 3.71429 4.85714C3.71429 5.16025 3.83469 5.45094 4.04902 5.66526C4.26335 5.87959 4.55404 6 4.85714 6C5.16025 6 5.45094 5.87959 5.66526 5.66526C5.87959 5.45094 6 5.16025 6 4.85714C6 4.55404 5.87959 4.26335 5.66526 4.04902C5.45094 3.83469 5.16025 3.71429 4.85714 3.71429Z"
                fill="currentColor"
              />
            </svg>
            <div class="lesson-kit__info-list">
              <p
                v-for="(ctx, index) in item.content.itemUsageContextList"
                :key="index"
              >
                <b>{{ ctx.name }}</b>에서 사용해요
              </p>
            </div>
          </div>

          <!-- 교육 과정 -->
          <div
            v-if="item.content.itemEducationInfoList?.length"
            class="lesson-kit__info"
          >
            <svg
              width="13"
              height="16"
              viewBox="0 0 13 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="lesson-kit__info-icon"
            >
              <path
                d="M6.51313 7.45756C5.65827 6.82556 4.37713 5.72042 0.703983 5.72042V13.6336C4.37713 13.6336 5.65827 14.7387 6.51313 15.3707M6.51313 7.45756V15.3707M6.51313 7.45756C7.36798 6.82556 8.64913 5.72042 12.3223 5.72042V13.6336C8.64913 13.6336 7.36798 14.7387 6.51313 15.3707M8.78284 0.633559V3.41299M10.8137 0.571845H3.18284C2.96964 0.571585 2.76063 0.630965 2.57941 0.743273L0.571411 1.99356L2.57941 3.24156C2.76063 3.35387 2.96964 3.41325 3.18284 3.41299H10.8137C11.0036 3.41837 11.1926 3.38563 11.3696 3.31669C11.5466 3.24775 11.7079 3.14402 11.8441 3.01163C11.9803 2.87924 12.0886 2.72087 12.1625 2.54591C12.2365 2.37095 12.2746 2.18293 12.2746 1.99299C12.2746 1.80304 12.2365 1.61503 12.1625 1.44007C12.0886 1.2651 11.9803 1.10674 11.8441 0.974347C11.7079 0.841955 11.5466 0.738222 11.3696 0.669283C11.1926 0.600345 11.0036 0.566458 10.8137 0.571845Z"
                stroke="currentColor"
                stroke-width="1.14286"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="lesson-kit__info-list">
              <p
                v-for="(edu, index) in item.content.itemEducationInfoList"
                :key="index"
              >
                {{ edu.curriculumSeries }} {{ edu.curriculumSeason }} -
                <b>{{ edu.itemTitle }}</b>
              </p>
            </div>
          </div>

          <!-- 키워드 -->
          <div
            v-if="item.content.itemKeywordList?.length"
            class="lesson-kit__keywords"
          >
            <span
              v-for="(kw, index) in item.content.itemKeywordList"
              :key="index"
              class="lesson-kit__keyword"
            >
              {{ formatKeyword(kw) }}
            </span>
          </div>

          <span class="lesson-kit__more">자세히 보기</span>
        </div>
      </li>
    </ul>

    <pagination
      v-if="pageInfo && pageInfo.page && pageInfo.total > size"
      :pageInfo="pageInfo"
      :page="pageInfo.page"
      :size="size"
      @updatePage="(num) => $emit('updatePage', num)"
    />
  </section>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import Pagination from "@/components/Pagination.vue";

const props = defineProps({
  // 콘텐츠가 있는 division 칩 ([{ division, count }], 노출 순서대로)
  chips: {
    type: Array,
    default: () => [],
  },
  // '전체' 칩에 표시할 개수
  totalCount: {
    type: Number,
    default: 0,
  },
  // 선택한 division ('' 이면 전체)
  activeDivision: {
    type: String,
    default: "",
  },
  // 그리드에 보여줄 콘텐츠 ([{ division, content }], division 을 모르면 null)
  items: {
    type: Array,
    default: () => [],
  },
  // 페이지 정보 (없으면 페이지네이션 미노출)
  pageInfo: {
    type: Object,
    default: null,
  },
  size: {
    type: Number,
    default: 25,
  },
  // 페이지 이동 중 여부
  loading: {
    type: Boolean,
    default: false,
  },
  // 선택한 부서/학기/과 라벨 (예: ["영유아부", "5과정2학기", "23과"])
  labels: {
    type: Array,
    default: () => [],
  },
  // 콘텐츠 썸네일 주소를 돌려주는 함수
  thumbnailOf: {
    type: Function,
    default: () => null,
  },
});

defineEmits(["open", "update:activeDivision", "updatePage"]);

// '전체'를 포함한 칩을 앞/뒤 절반씩 두 줄로 나눔 (division '' 이 전체)
const chipRows = computed(() => {
  const allChips = [
    { division: "", label: "전체", count: props.totalCount },
    ...props.chips.map((chip) => ({ ...chip, label: chip.division })),
  ];
  const half = Math.ceil(allChips.length / 2);
  return [allChips.slice(0, half), allChips.slice(half)].filter(
    (row) => row.length > 0,
  );
});

/** 대상 부서명을 한 줄로 합친다 */
const groupNamesOf = (content) =>
  (content?.targetGroupList ?? [])
    .map((group) => group.groupNameValue)
    .filter(Boolean)
    .join(" · ");

/** 대상 그룹을 종류(연령/역할)별로 묶어 문구를 붙인다 */
const groupedTargetsOf = (list) => {
  if (!list || list.length === 0) return [];

  const groupedMap = list.reduce((acc, item) => {
    if (!acc[item.groupType]) {
      acc[item.groupType] = [];
    }
    acc[item.groupType].push(item.groupNameValue);
    return acc;
  }, {});

  return Object.keys(groupedMap).map((type) => {
    let suffix = "";
    if (type === "AGE_GROUP") {
      suffix = " 활동에 좋아요";
    } else if (type === "ROLES") {
      suffix = "님께 추천해요";
    }
    return { names: groupedMap[type].join(", "), suffix };
  });
};

const formatKeyword = (item) =>
  item.depth1 === item.depth2 ? item.depth1 : `${item.depth1} ${item.depth2}`;
</script>

<style scoped>
/* ── 디자인 토큰 (하나 바이블 페이지들과 동일) ── */
.lesson-kit {
  --hb-navy-900: #07235e;
  --hb-navy-700: #0c3388;
  --hb-navy-500: #1c4bb0;
  --hb-navy-300: #2f98e4;
  --hb-band-sky: #eff5fb;
  --hb-ink-700: #212121;
  --hb-ink-500: #525252;
  --hb-gray-200: #eaeaea;
  --hb-hairline: rgb(12 51 136 / 0.07);
  --hb-radius: 0.5rem;
  --hb-radius-pill: 999px;
  --hb-shadow-sm: 0 2px 10px rgb(12 51 136 / 0.06);

  color: var(--hb-ink-700);
}

/* ── 요약 ── */
.lesson-kit__head {
  padding: 2rem 2.5rem;
  border: 1px solid var(--hb-hairline);
  border-radius: 1.25rem;
  background: var(--hb-band-sky);
}

.lesson-kit__eyebrow {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--hb-navy-500);
}

.lesson-kit__title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  margin-top: 0.375rem;
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.03em;
  color: var(--hb-navy-900);
  word-break: keep-all;
}

.lesson-kit__dot {
  margin: 0 0.5rem;
  color: var(--hb-navy-300);
}

.lesson-kit__desc {
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--hb-ink-500);
  word-break: keep-all;
}

.lesson-kit__desc b {
  font-weight: 700;
  color: var(--hb-navy-700);
  white-space: nowrap;
}

/* ── 종류 필터 ── */
.lesson-kit__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* 줄 구분은 모바일에서만 사용 (그 외엔 칩이 바로 .lesson-kit__chips 의 항목이 됨) */
.lesson-kit__chip-row {
  display: contents;
}

.lesson-kit__chip {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  border: 1px solid rgb(12 51 136 / 0.12);
  border-radius: var(--hb-radius-pill);
  background: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--hb-ink-700);
  white-space: nowrap;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;
}

.lesson-kit__chip-count {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--hb-navy-300);
}

.lesson-kit__chip.is-active {
  border-color: var(--hb-navy-700);
  background: var(--hb-navy-700);
  color: #fff;
}

.lesson-kit__chip.is-active .lesson-kit__chip-count {
  color: rgb(255 255 255 / 0.75);
}

/* ── 콘텐츠 그리드 ── */
.lesson-kit__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 2rem 1.5rem;
  margin-top: 2.5rem;
  padding: 0;
  list-style: none;
  transition: opacity 0.2s ease;
}

.lesson-kit__grid.is-loading {
  opacity: 0.5;
  pointer-events: none;
}

.lesson-kit__card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}

.lesson-kit__thumb {
  position: relative;
  overflow: hidden;
  aspect-ratio: 3 / 2;
  border: 1px solid var(--hb-hairline);
  border-radius: var(--hb-radius);
  background: var(--hb-gray-200);
}

.lesson-kit__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lesson-kit__card:focus-visible {
  outline: none;
}

.lesson-kit__card:focus-visible .lesson-kit__thumb {
  outline: 2px solid var(--hb-navy-500);
  outline-offset: 2px;
}

.lesson-kit__badge {
  position: absolute;
  top: 0.625rem;
  left: 0.625rem;
  padding: 0.25rem 0.625rem;
  border-radius: var(--hb-radius-pill);
  background: rgb(255 255 255 / 0.92);
  box-shadow: var(--hb-shadow-sm);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  color: var(--hb-navy-700);
}

.lesson-kit__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.lesson-kit__group {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--hb-navy-300);
}

/* ── 마우스오버 상세 정보 ── */
.lesson-kit__item {
  position: relative;
}

/* 터치 기기·모바일에서는 hover 가 없으므로 숨김 */
.lesson-kit__overlay {
  display: none;
}

@media (hover: hover) and (min-width: 640px) {
  .lesson-kit__overlay {
    position: absolute;
    top: 0;
    left: 50%;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: calc(100% + 2rem);
    min-height: 100%;
    padding: 1rem;
    border: 1px solid var(--hb-gray-200);
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 10px 30px rgb(12 51 136 / 0.14);
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%);
    transition: opacity 0.3s ease;
  }

  .lesson-kit__item:hover .lesson-kit__overlay {
    z-index: 20;
    opacity: 1;
    pointer-events: auto;
  }
}

.lesson-kit__overlay-title {
  font-weight: 600;
  line-height: 1.35;
  word-break: keep-all;
}

.lesson-kit__info {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.lesson-kit__info-icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: var(--hb-navy-500);
}

.lesson-kit__info-list {
  display: grid;
  gap: 0.25rem;
  font-size: 0.9375rem;
  line-height: 1.35;
  color: var(--hb-ink-500);
  word-break: keep-all;
}

.lesson-kit__info-list b {
  font-weight: 600;
  color: var(--hb-ink-700);
}

.lesson-kit__keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.lesson-kit__keyword {
  padding: 0.25rem 0.5rem;
  border: 1px solid rgb(12 51 136 / 0.2);
  border-radius: var(--hb-radius-pill);
  font-size: 0.8125rem;
  line-height: 1.3;
  color: var(--hb-ink-500);
}

.lesson-kit__more {
  margin-top: auto;
  padding: 0.75rem;
  border: 1px solid var(--hb-gray-200);
  border-radius: var(--hb-radius);
  text-align: center;
  transition: background-color 0.2s ease;
}

.lesson-kit__more:hover {
  background: var(--hb-band-sky);
}

/* ── 반응형 ── */
@media (max-width: 1023px) {
  .lesson-kit__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .lesson-kit__head {
    padding: 0.875rem;
    border-radius: var(--hb-radius-lg);
  }

  /* 모바일은 칩을 두 줄로 나눠 함께 가로 스크롤 (스크롤바는 숨김) */
  .lesson-kit__chips {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    overflow-x: auto;
    margin-right: -1.25rem;
    scrollbar-width: none;
  }

  .lesson-kit__chips::-webkit-scrollbar {
    display: none;
  }

  .lesson-kit__chip-row {
    display: flex;
    gap: 0.5rem;
    padding-right: 1.25rem;
  }

  .lesson-kit__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem 0.75rem;
    margin-top: 1.75rem;
  }

  .lesson-kit__card {
    gap: 0.5rem;
  }
}
</style>
